// src/components/tabs/TabBlog.jsx
// Mirrors relblog.inc — list and post comments with @mention support

import React, { useState, useEffect, useRef } from 'react';
import { tabsApi } from '../../services/api';
import { useTexts, TXT } from '../../context/TextContext';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/formatDate';

// Extrae el texto raw con tags del DOM del contenteditable
function extractRaw(el) {
  let raw = '';
  el.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      raw += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.dataset.tag) {
        raw += node.dataset.tag;
      } else if (node.tagName === 'BR') {
        raw += '\n';
      } else {
        raw += node.textContent;
      }
    }
  });
  return raw;
}

// Mueve el cursor al final del contenteditable
function moveCursorToEnd(el) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

export default function TabBlog({ docId, onHide }) {
  const { t, idiId } = useTexts();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // @mention state
  const [mentionQuery, setMentionQuery]   = useState('');
  const [mentionOptions, setMentionOptions] = useState([]);
  const [showMention, setShowMention]     = useState(false);
  const [mentionIndex, setMentionIndex]   = useState(0);
  const editorRef      = useRef();
  const debounceRef    = useRef();
  const mentionRangeRef = useRef(null);
  const mentionListRef  = useRef(null);

  useEffect(() => { load(); }, [docId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await tabsApi.getBlog(docId);
      setData(res.data);
    } catch { setData({ canView: false, canEdit: false, entries: [] }); }
    finally { setLoading(false); }
  };

  // ── @mention logic ────────────────────────────────────────────────────────
  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    setIsEmpty(el.textContent.trim() === '');

    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);

    // Solo procesamos si el cursor está en un nodo de texto
    if (range.startContainer.nodeType !== Node.TEXT_NODE) {
      setShowMention(false);
      return;
    }

    const textBeforeCursor = range.startContainer.textContent.slice(0, range.startOffset);
    const atMatch = textBeforeCursor.match(/@([^\s@]*)$/);

    if (atMatch) {
      const q = atMatch[1];
      setMentionQuery(q);
      setShowMention(true);

      // Guardamos el rango del nodo de texto que contiene el @
      mentionRangeRef.current = {
        node: range.startContainer,
        atIndex: textBeforeCursor.lastIndexOf('@'),
        queryLen: q.length,
      };

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        tabsApi.searchMentions(q)
          .then(res => { setMentionOptions(res.data || []); setMentionIndex(0); })
          .catch(() => { setMentionOptions([]); setMentionIndex(0); });
      }, 250);
    } else {
      setShowMention(false);
      setMentionOptions([]);
    }
  };

  const insertMention = (item) => {
    const ref = mentionRangeRef.current;
    if (!ref) return;

    const { node, atIndex, queryLen } = ref;
    const fullText = node.textContent;
    const before = fullText.slice(0, atIndex);       // texto antes del @
    const after  = fullText.slice(atIndex + 1 + queryLen); // texto después de lo escrito

    // Crear el span de mención (no editable)
    const tag = `@[${item.name}](${item.tipo}:${item.id})`;
    const mentionSpan = document.createElement('span');
    mentionSpan.dataset.tag = tag;
    mentionSpan.contentEditable = 'false';
    mentionSpan.style.color = '#70b9b0';
    mentionSpan.style.fontWeight = '600';
    mentionSpan.textContent = `@${item.name}`;

    // Espacio después de la mención
    const space = document.createTextNode(' ');

    // Reemplazar el nodo de texto original con: before + mentionSpan + space + after
    const parent = node.parentNode;
    const beforeNode = document.createTextNode(before);
    const afterNode  = document.createTextNode(after);

    parent.insertBefore(beforeNode, node);
    parent.insertBefore(mentionSpan, node);
    parent.insertBefore(space, node);
    parent.insertBefore(afterNode, node);
    parent.removeChild(node);

    // Mover cursor después del espacio
    const sel = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(space);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    setShowMention(false);
    setMentionOptions([]);
    setIsEmpty(false);
    editorRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showMention || mentionOptions.length === 0) return;
    if (e.key === 'Escape') {
      setShowMention(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(i => {
        const next = Math.min(i + 1, mentionOptions.length - 1);
        scrollMentionItem(next);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(i => {
        const prev = Math.max(i - 1, 0);
        scrollMentionItem(prev);
        return prev;
      });
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(mentionOptions[mentionIndex]);
    }
  };

  const scrollMentionItem = (index) => {
    const list = mentionListRef.current;
    if (!list) return;
    const item = list.children[index];
    if (item) item.scrollIntoView({ block: 'nearest' });
  };

  // ── Post ──────────────────────────────────────────────────────────────────
  const handlePost = async () => {
    const el = editorRef.current;
    if (!el) return;
    const raw = extractRaw(el).trim();
    if (!raw) return;
    setPosting(true);
    try {
      await tabsApi.postBlog(docId, raw);
      el.innerHTML = '';
      setIsEmpty(true);
      setShowMention(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    } finally { setPosting(false); }
  };

  // ── Render del texto con @menciones resaltadas (lista de comentarios) ─────
  const renderText = (raw) => {
    const parts = raw.split(/(@\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      const m = part.match(/^@\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        return <span key={i} style={{ color: '#70b9b0', fontWeight: 600 }}>@{m[1]}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (loading) return <div className="text-center p-3"><div className="spinner-border spinner-border-sm" /></div>;
  if (!data?.canView) { if (onHide) onHide(); return null; }

  return (
    <div className="container-fluid">

      {/* New comment input */}
      {data.canEdit && (
        <div className="row mt-2">
          <div className="col-sm-10 mt-2 mb-2 valor" style={{ position: 'relative' }}>
            {/* Placeholder manual porque contenteditable no soporta placeholder nativo */}
            {isEmpty && (
              <div style={{
                position: 'absolute', top: 0, left: 0, padding: '4px 2px',
                color: '#aaa', pointerEvents: 'none', userSelect: 'none', fontSize: '0.95rem',
              }}>
                {t(TXT.POST, 'Escribir comentario')}… {t(TXT.MENTION_HINT, '@ para mencionar')}
              </div>
            )}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              style={{
                border: 'none', outline: 0, minWidth: '100%',
                minHeight: '80px', padding: '4px 2px',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontSize: '0.95rem', lineHeight: '1.5',
              }}
            />

            {/* Dropdown de menciones */}
            {showMention && mentionOptions.length > 0 && (
              <ul ref={mentionListRef} className="list-group shadow"
                style={{ position: 'absolute', zIndex: 9999, top: '100%', left: 0, minWidth: '240px', maxHeight: '220px', overflowY: 'auto' }}>
                {mentionOptions.map((item, idx) => (
                  <li key={`${item.tipo}-${item.id}`}
                    className="list-group-item list-group-item-action py-1 d-flex align-items-center gap-2"
                    style={{ cursor: 'pointer', fontSize: '0.88rem', background: idx === mentionIndex ? '#e8f5e9' : '' }}
                    onMouseEnter={() => setMentionIndex(idx)}
                    onMouseDown={e => { e.preventDefault(); insertMention(item); }}
                    onTouchEnd={e => { e.preventDefault(); insertMention(item); }}>
                    <i className={`fa-solid fa-1x ${item.tipo === 'G' ? 'fa-users' : 'fa-user'}`}
                       style={{ color: '#70b9b0', width: '16px' }} />
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="col-sm-1 mt-2 mb-2">
            <button className="button color1" onClick={handlePost} disabled={posting}>
              <i className="fa-solid fa-list fa-1x color1" />
              &nbsp;&nbsp;{posting ? '…' : t(TXT.POST, 'Postear')}&nbsp;
            </button>
          </div>
        </div>
      )}

      {/* Comment entries */}
      {data.entries.map((e, idx) => (
        <div className="row mt-3 mb-2" key={idx}>
          <div className="col-12">
            <strong>{e.author}</strong>&nbsp;&nbsp;
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
              {formatDate(e.date, user?.timezone, idiId, { showTime: true })}
            </span>
          </div>
          <div className="col-12 border-bottom">{renderText(e.text)}</div>
        </div>
      ))}

    </div>
  );
}
